export type ServicePage = {
  path: string;
  eyebrow: string;
  intro: string;
  overview: string;
  image: string;
  scope: string[];
  process: { title: string; text: string }[];
  proof: { label: string; path: string };
};

export const servicePages: Record<string, ServicePage> = {
  '/residential-development-design-melbourne': {
    path: '/residential-development-design-melbourne',
    eyebrow: 'Residential Development',
    intro: 'Clear, site-responsive design for Melbourne townhouse and multi-unit projects.',
    overview: 'We bring feasibility, planning documentation, design development and detailed drawings into one coordinated process. The aim is a well-resolved scheme that responds to the site, the brief and the planning context.',
    image: '/images/u018068_tp_03_.webp',
    scope: ['Site and development-potential review', 'Master planning and feasibility studies', 'Concept design and 3D visualisation', 'Town planning documentation', 'Working drawings and detailing', 'Consultant and submission coordination'],
    process: [
      { title: 'Assess', text: 'Review the site, planning context, constraints and development brief.' },
      { title: 'Resolve', text: 'Test layouts and built form through concept design and visual studies.' },
      { title: 'Document', text: 'Prepare coordinated drawings and information for the agreed project stage.' },
      { title: 'Coordinate', text: 'Keep consultants, submissions and delivery milestones moving together.' },
    ],
    proof: { label: 'View the Mitcham townhouse project', path: '/portfolio/mitcham-townhouse-subdivision' },
  },
  '/custom-home-design-melbourne': {
    path: '/custom-home-design-melbourne',
    eyebrow: 'Custom Residential',
    intro: 'Considered Melbourne homes shaped around their site, occupants and long-term use.',
    overview: 'Our custom-home process connects spatial planning, exterior form, interiors and documentation. Each decision is tested against the way you want to live, the character of the site and the practical requirements of delivery.',
    image: '/images/toorak_luxury_04_20swim.webp',
    scope: ['Site-responsive concept design', 'Floor plans and exterior studies', '3D visualisation', 'Interior planning and material palettes', 'Custom joinery and lighting coordination', 'Detailed drawing packages'],
    process: [
      { title: 'Brief', text: 'Define priorities, lifestyle needs, site opportunities and project parameters.' },
      { title: 'Explore', text: 'Develop and compare spatial, material and built-form directions.' },
      { title: 'Refine', text: 'Coordinate the preferred scheme across exterior and interior decisions.' },
      { title: 'Detail', text: 'Translate the resolved design into a clear drawing and selections package.' },
    ],
    proof: { label: 'Tour the Toorak luxury residence', path: '/portfolio/toorak-luxury-residence' },
  },
  '/commercial-building-design-melbourne': {
    path: '/commercial-building-design-melbourne',
    eyebrow: 'Commercial & Hospitality',
    intro: 'Practical, distinctive spaces for Melbourne retail, hospitality and mixed-use projects.',
    overview: 'Commercial spaces need to work for customers, staff and operators at the same time. We coordinate layout, customer experience, material choices and documentation around the realities of the venue and project brief.',
    image: '/images/u018067_cafe_01_.webp',
    scope: ['Retail and hospitality space planning', 'Fit-out concepts and documentation', 'Customer-flow and staff-workflow studies', 'Material, finish and lighting selection', 'Custom joinery and display design', 'Consultant and project coordination'],
    process: [
      { title: 'Understand', text: 'Map the operating model, users, site conditions and commercial brief.' },
      { title: 'Plan', text: 'Resolve flow, zoning, capacity and the customer experience.' },
      { title: 'Express', text: 'Develop a coherent spatial, material and visual identity.' },
      { title: 'Deliver', text: 'Prepare coordinated information and support the agreed delivery stages.' },
    ],
    proof: { label: 'See the Carlton cafe fit-out', path: '/portfolio/carlton-cafe-patisserie' },
  },
  '/interior-design-melbourne': {
    path: '/interior-design-melbourne',
    eyebrow: 'Interior Design',
    intro: 'Integrated interiors for Melbourne homes, hospitality venues and retail spaces.',
    overview: 'We treat interiors as part of the whole project rather than a final decorative layer. Planning, joinery, finishes and lighting are coordinated early so the result feels intentional and supports everyday use.',
    image: '/images/service-interior-design.webp',
    scope: ['Space planning and furniture layouts', 'Kitchen, bathroom and joinery design', 'Materials and finish palettes', 'Lighting concepts and specifications', 'FF&E selection and schedules', 'Interior documentation'],
    process: [
      { title: 'Discover', text: 'Understand the users, functional needs, atmosphere and budget priorities.' },
      { title: 'Compose', text: 'Build the spatial layout, palette and key design moments.' },
      { title: 'Coordinate', text: 'Align joinery, finishes, lighting and relevant consultant information.' },
      { title: 'Specify', text: 'Create a clear selections and documentation package for delivery.' },
    ],
    proof: { label: 'View the Toorak residence interiors', path: '/portfolio/toorak-luxury-residence' },
  },
  '/planning-project-management-melbourne': {
    path: '/planning-project-management-melbourne',
    eyebrow: 'Project Coordination',
    intro: 'A connected path from early planning through documentation, construction and handover.',
    overview: 'We help clients organise the people, information and decisions that keep a project progressing. The scope is tailored to the project and may include consultant briefing, submission coordination, tender support and delivery-stage communication.',
    image: '/images/service-project-management-optimized.webp',
    scope: ['Project scope and milestone planning', 'Consultant briefing and coordination', 'Council and submission liaison', 'Tender and builder-selection support', 'Construction-stage coordination', 'Handover and settlement support'],
    process: [
      { title: 'Define', text: 'Set the scope, responsibilities, decision points and practical milestones.' },
      { title: 'Organise', text: 'Bring the required consultants and information into a coordinated workflow.' },
      { title: 'Track', text: 'Monitor actions, decisions and documentation through each agreed stage.' },
      { title: 'Support', text: 'Maintain continuity through delivery, handover and close-out activities.' },
    ],
    proof: { label: 'Read the Mitcham delivery story', path: '/portfolio/mitcham-townhouse-subdivision' },
  },
};
